import React from "react";

function numberBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function moneyBR(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const x = Number(n);
  return x.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function Tag({ children, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
        tones[tone] || tones.slate
      }`}
    >
      {children}
    </span>
  );
}

function MetricPill({ label, value, tone = "slate" }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-200",
    rose: "bg-rose-50 text-rose-700 ring-rose-200",
    sky: "bg-sky-50 text-sky-700 ring-sky-200",
  };
  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${
        tones[tone] || tones.slate
      }`}
    >
      <span className="opacity-80">{label}</span>
      <span className="font-bold">{value}</span>
    </div>
  );
}

function BaseCompactRow({ title, subtitle, tone = "slate", children }) {
  const tones = {
    emerald: "bg-emerald-500",
    rose: "bg-rose-500",
    amber: "bg-amber-500",
    sky: "bg-sky-500",
    slate: "bg-slate-500",
  };

  return (
    <div className="py-3">
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 md:col-span-3 flex items-center gap-3">
          <span
            className={`h-2.5 w-2.5 rounded-full ${tones[tone] ||
              tones.slate}`}
          />
          <div>
            <div className="text-[12px] uppercase tracking-wider text-slate-600">
              {title}
            </div>
          </div>
        </div>
        <div className="col-span-12 md:col-span-9 flex flex-wrap gap-2 justify-start md:justify-end">
          {children}
        </div>
      </div>
    </div>
  );
}

export function MotorCredito({ data }) {
  const d = data || {};
  const baseI = d.baseI || {};
  const baseII = d.baseII || {};
  const baseIII = d.baseIII || {};
  const baseIV = d.baseIV || {};

  const statusTag =
    d.statusRetorno === 1
      ? { text: "Status OK", tone: "green" }
      : { text: "Status com falha", tone: "rose" };

  const rawDoc =
    d?.dadosCadastrais?.cpf ||
    d?.dadosCadastrais?.documento ||
    d?.dadosCadastrais?.cnpj ||
    "";
  const onlyDigits = String(rawDoc).replace(/\D/g, "");
  const isCPF =
    Boolean(d?.dadosCadastrais?.cpf) ||
    (onlyDigits.length > 0 && onlyDigits.length <= 11);

  return (
    <div className="w-full mt-5">
      <div className="mx-auto">
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2 justify-between">
            <div className="flex items-center justify-between flex-1">
              <div className="text-base font-semibold text-slate-900">
                Resumo da consulta
              </div>
            </div>
          </div>

          <div className="px-4">
            <BaseCompactRow title="Base I" subtitle="Restrições e Perfil" tone="emerald">
              <MetricPill
                label="Protestos"
                value={`${numberBR(baseI.protestosQuantidade)} | ${moneyBR(
                  baseI.protestosValorTotal
                )}`}
                tone="rose"
              />
              <MetricPill
                label="Pendências"
                value={`${numberBR(baseI.pendenciasQuantidade)} | ${moneyBR(
                  baseI.pendenciasValorTotal
                )}`}
                tone="amber"
              />
              <MetricPill
                label="Cheques"
                value={numberBR(baseI.chequesSemFundoQuantidade)}
                tone="slate"
              />
              <MetricPill
                label="Ações"
                value={numberBR(baseI.acoesCiveisQuantidade)}
                tone="slate"
              />
              {!isCPF && (
                <MetricPill
                  label="Falência/Recup."
                  value={numberBR(baseI.falenciasRecJudiciaisQuantidade)}
                  tone="slate"
                />
              )}
              {!isCPF && (
                <MetricPill
                  label="Fundação"
                  value={baseI.dataFundacao || "—"}
                  tone="sky"
                />
              )}
            </BaseCompactRow>

            <div className="border-t border-slate-200" />

            <BaseCompactRow title="Base II" subtitle="Dados Complementares" tone="rose">
              <MetricPill
                label="Status"
                value={baseII.fontes?.length ? "Com dados" : "Sem dados"}
                tone={baseII.fontes?.length ? "emerald" : "slate"}
              />
            </BaseCompactRow>

            <div className="border-t border-slate-200" />

            <BaseCompactRow title="Base III" subtitle="Registros Adicionais" tone="amber">
              <MetricPill
                label="Status"
                value={baseIII.fontes?.length ? "Com dados" : "Sem dados"}
                tone={baseIII.fontes?.length ? "emerald" : "slate"}
              />
            </BaseCompactRow>

            <div className="border-t border-slate-200" />

            <BaseCompactRow title="Base IV" subtitle="Consolidação" tone="sky">
              <MetricPill
                label="Status"
                value={baseIV.fontes?.length ? "Com dados" : "Sem dados"}
                tone={baseIV.fontes?.length ? "emerald" : "slate"}
              />
            </BaseCompactRow>
          </div>
        </div>
      </div>
    </div>
  );
}
